/**
 * Admin UsersThree List
 * MagnifyingGlass, filter, manage all platform users
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BACKEND_URL } from '../../config';
import toast from '../../utils/toast';

import {
  Buildings,
  Calendar,
  CheckCircle,
  CircleNotch,
  Clock,
  DotsThreeVertical,
  Envelope,
  Eye,
  Funnel,
  MagnifyingGlass,
  Prohibit,
  UsersThree,
  WarningCircle
} from '@phosphor-icons/react';
const UsersList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_desc');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchUserStats();
  }, [statusFilter, planFilter, sortBy]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (planFilter !== 'all') params.append('plan', planFilter);
      
      const response = await fetch(
        `${BACKEND_URL}/api/admin/users?${params.toString()}`,
        {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        toast.error('Failed to load users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/users/stats`,
        {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        // Fallback: calculate from loaded users
        const total = users.length;
        const partnerPro = users.filter(u => u.plan === 'partner-pro' || u.plan === 'partner_pro').length;
        const pro = users.filter(u => u.plan === 'pro').length;
        const suspended = users.filter(u => u.status === 'suspended').length;
        setStats({ total_users: total, partner_pro_users: partnerPro, pro_users: pro, suspended_users: suspended });
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  const handleSuspendUser = async (userId) => {
    if (!window.confirm('Are you sure you want to suspend this user?')) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/users/${userId}/status`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: 'suspended' })
        }
      );

      if (response.ok) {
        toast.success('User suspended');
        fetchUsers();
      } else {
        toast.error('Failed to suspend user');
      }
    } catch (error) {
      console.error('Error suspending user:', error);
      toast.error('Failed to suspend user');
    }
  };

  const filteredUsers = users
    .filter(user => {
      const matchesSearch = 
        user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.id?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'created_desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'created_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'name_asc':
          return (a.name || '').localeCompare(b.name || '');
        case 'name_desc':
          return (b.name || '').localeCompare(a.name || '');
        default:
          return 0;
      }
    });

  const getPlanBadge = (plan) => {
    const colors = {
      basic: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      pro: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'partner-pro': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      partner_pro: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    };
    return colors[plan?.toLowerCase()] || colors.basic;
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle };
      case 'suspended':
        return { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: Prohibit };
      case 'pending':
        return { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock };
      default:
        return { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: WarningCircle };
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-white/10 rounded animate-pulse w-64"></div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-24 bg-white/10 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">User Management</h1>
        <p className="text-gray-400">Manage registered users, plans, and account status</p>
      </div>

      {/* Summary Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <UsersThree size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-400">Total UsersThree</p>
                <p className="text-2xl font-bold text-white">{stats.total_users || users.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                <UsersThree size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-400">Partner Pro</p>
                <p className="text-2xl font-bold text-white">{stats.partner_pro_users || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                <CheckCircle size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-400">Pro UsersThree</p>
                <p className="text-2xl font-bold text-white">{stats.pro_users || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                <Prohibit size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-400">Suspended</p>
                <p className="text-2xl font-bold text-white">{stats.suspended_users || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* MagnifyingGlass */}
          <div className="relative md:col-span-2">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>

          {/* Plan Funnel */}
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="all">All Plans</option>
            <option value="basic">Basic</option>
            <option value="pro">Pro</option>
            <option value="partner_pro">Partner Pro</option>
          </select>

          {/* Status Funnel */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="pending">Pending</option>
          </select>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="created_desc">Newest First</option>
            <option value="created_asc">Oldest First</option>
            <option value="name_asc">Name (A-Z)</option>
            <option value="name_desc">Name (Z-A)</option>
          </select>
        </div>
      </div>

      {/* UsersThree List */}
      <div className="space-y-4">
        {filteredUsers.map((user, index) => {
          const statusBadge = getStatusBadge(user.status);
          const StatusIcon = statusBadge.icon;

          return (
            <motion.div
              key={user.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => navigate(`/admin/users/${user.id}`)}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-[#6366F1]/30 transition-all cursor-pointer group"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center flex-shrink-0 text-white font-bold text-lg">
                    {user.name?.charAt(0).toUpperCase() || 'U'}
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Name & Badges */}
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-white group-hover:text-[#6366F1] transition-colors">
                        {user.name || 'Unnamed User'}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPlanBadge(user.plan)}`}>
                        {user.plan === 'partner_pro' || user.plan === 'partner-pro' ? 'Partner Pro' : 
                         user.plan === 'pro' ? 'Pro' : 'Basic'}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border flex items-center gap-1 ${statusBadge.color}`}>
                        <StatusIcon size={12} />
                        {user.status || 'active'}
                      </span>
                    </div>

                    {/* Email */}
                    <div className="flex items-center gap-2 text-sm text-gray-400 mb-3">
                      <Envelope size={14} />
                      {user.email}
                    </div>

                    {/* Metadata Grid */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="flex items-center gap-2">
                        <Buildings size={16} className="text-gray-500" />
                        <div>
                          <p className="text-xs text-gray-500">Organizations</p>
                          <p className="text-sm font-medium text-white">
                            {user.organizations?.length || 0}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-gray-500" />
                        <div>
                          <p className="text-xs text-gray-500">Joined</p>
                          <p className="text-sm font-medium text-white">
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={16} className="text-gray-500" />
                        <div>
                          <p className="text-xs text-gray-500">Last Login</p>
                          <p className="text-sm font-medium text-white">
                            {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  {user.status !== 'suspended' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSuspendUser(user.id);
                      }}
                      className="p-2 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                      title="Suspend User"
                    >
                      <Prohibit size={18} />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin/users/${user.id}`);
                    }}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title="View Details"
                  >
                    <Eye size={18} />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredUsers.length === 0 && !loading && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-12 text-center">
          <UsersThree size={48} className="text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No UsersThree Found</h3>
          <p className="text-gray-400">
            {searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'No users in the system yet'}
          </p>
        </div>
      )}
    </div>
  );
};

export default UsersList;
